/**
 * 檔案用途區塊
 * @module WalletConnect Token Network Adapter Mapping
 * @purpose 維護錢包連線流程所需的幣種、網路、CAIP chainId 與 adapter 類型對應，並提供查詢與 CAIP namespace 解析工具。
 * @external https://innotech.atlassian.net/browse/IN-111889
 * @external https://innotech.atlassian.net/browse/IN-111892
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
import { AppKitNetwork, mainnet, classic, polygon, sepolia } from '@reown/appkit/networks';
import { defineChain } from 'viem/chains/utils';

/**
 * 宣告內容用途說明與單號關聯
 * @description 定義 ETC 測試網 Mordor 鏈參數，補齊 viem 與 AppKit 未內建鏈的網路資訊。
 * @purpose 讓支付與簽章流程可在 eip155:63 上建立一致的 chain metadata（RPC、瀏覽器、原生幣資訊）。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
const mordor = defineChain({
  id: 63,
  name: 'Mordor',
  // 建議加這個，Reown 或某些 UI 庫會用它來判斷是否顯示 "Testnet" 標籤
  testnet: true,
  nativeCurrency: {
    decimals: 18,
    name: 'Mordor Ether', // 通常用 Mordor Ether 或 METC 都可以
    symbol: 'METC',
  },
  rpcUrls: {
    default: {
      http: ['https://rpc.mordor.etccooperative.org'],
    },
    // Viem 的標準類型通常建議同時定義 public
    public: {
      http: ['https://rpc.mordor.etccooperative.org'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Blockscout',
      url: 'https://etc-mordor.blockscout.com',
      // 如果你的 App 需要驗證合約，加這個會有幫助
      apiUrl: 'https://etc-mordor.blockscout.com/api',
    },
  },
});

/**
 * 宣告內容用途說明與單號關聯
 * @description 定義目前錢包連線流程允許的 adapter 類型聯集。
 * @purpose 以型別約束映射資料與 adapter 蒐集邏輯，避免接入未支援的鏈協定。
 */
export type AdapterType = 'wagmi' | 'solana' | 'ton';

/**
 * 宣告內容用途說明與單號關聯
 * @description 描述單一幣種-網路映射資料結構，包含 chainId、network、adapter 與 ERC20 輔助欄位。
 * @purpose 統一 WalletConnect 查詢層與 UI/流程層對映射欄位的讀取契約。
 */
export interface TokenNetworkAdapterMapping {
  /** 後端傳入的 token 符號（如 ETH, BTC, USDT_ERC20） */
  token: string;
  /** 正規化後的網路鍵，用於 (currency, networkName) 查詢（如 mainnet, sepolia, bitcoin） */
  networkKey: string;
  /** CAIP-2 格式的 Chain ID（如 eip155:1） */
  chainId: string;
  /** AppKit Network 物件 */
  network: AppKitNetwork;
  /** Adapter 類型 */
  adapterType: AdapterType;
  /** WalletConnect namespace (eip155, bip122, tron 等) */
  namespace: string;
  /** 網路名稱（用於顯示） */
  networkName: string;
  /** 是否為 ERC20 token */
  isERC20?: boolean;
  /** Token decimals（ERC20 時可選，亦可由 URL 參數 decimals 取得） */
  decimals?: number;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 維護幣種對多網路的映射主表，承載 chainId、network、adapterType 與顯示名稱。
 * @purpose 作為所有 token/network 查詢函式的單一來源，確保錢包連線選網與 adapter 初始化一致。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export const TOKEN_NETWORK_ADAPTER_MAPPING: Record<string, TokenNetworkAdapterMapping[]> = {
  ETH: [
    {
      token: 'ETH',
      networkKey: 'mainnet',
      chainId: 'eip155:1',
      network: mainnet,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Ethereum Mainnet',
    },
    {
      token: 'ETH',
      networkKey: 'sepolia',
      chainId: 'eip155:11155111',
      network: sepolia,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Sepolia',
    },
  ],
  ETC: [
    {
      token: 'ETC',
      networkKey: 'ethereum classic',
      chainId: 'eip155:61',
      network: classic,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Ethereum Classic',
    },
    {
      token: 'ETC',
      networkKey: 'mordor',
      chainId: 'eip155:63',
      network: mordor as AppKitNetwork,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Mordor',
    },
  ],
  USDT: [
    {
      token: 'USDT',
      networkKey: 'mainnet',
      chainId: 'eip155:1',
      network: mainnet,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Ethereum Mainnet (ERC20)',
      isERC20: true,
      decimals: 6,
    },
    {
      token: 'USDT',
      networkKey: 'sepolia',
      chainId: 'eip155:11155111',
      network: sepolia,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Sepolia (ERC20)',
      isERC20: true,
      decimals: 6,
    },
  ],
  USDC_ERC20: [
    {
      token: 'USDC',
      networkKey: 'mainnet',
      chainId: 'eip155:1',
      network: mainnet,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Ethereum Mainnet (ERC20)',
      isERC20: true,
      decimals: 6,
    },
    {
      token: 'USDC',
      networkKey: 'sepolia',
      chainId: 'eip155:11155111',
      network: sepolia,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Sepolia (ERC20)',
      isERC20: true,
      decimals: 6,
    },
  ],
  DAI_ERC20: [
    {
      token: 'DAI',
      networkKey: 'mainnet',
      chainId: 'eip155:1',
      network: mainnet,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Ethereum Mainnet (ERC20)',
      isERC20: true,
      decimals: 18,
    },
    {
      token: 'DAI',
      networkKey: 'sepolia',
      chainId: 'eip155:11155111',
      network: sepolia,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Sepolia (ERC20)',
      isERC20: true,
      decimals: 18,
    },
  ],
  WBTC_ERC20: [
    {
      token: 'WBTC',
      networkKey: 'mainnet',
      chainId: 'eip155:1',
      network: mainnet,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Ethereum Mainnet (ERC20)',
      isERC20: true,
      decimals: 8,
    },
    {
      token: 'WBTC',
      networkKey: 'sepolia',
      chainId: 'eip155:11155111',
      network: sepolia,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Sepolia (ERC20)',
      isERC20: true,
      decimals: 8,
    },
  ],
  AAVE_ERC20: [
    {
      token: 'AAVE',
      networkKey: 'mainnet',
      chainId: 'eip155:1',
      network: mainnet,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Ethereum Mainnet (ERC20)',
      isERC20: true,
      decimals: 18,
    },
    {
      token: 'AAVE',
      networkKey: 'sepolia',
      chainId: 'eip155:11155111',
      network: sepolia,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Sepolia (ERC20)',
      isERC20: true,
      decimals: 18,
    },
  ],
  UNI_ERC20: [
    {
      token: 'UNI',
      networkKey: 'mainnet',
      chainId: 'eip155:1',
      network: mainnet,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Ethereum Mainnet (ERC20)',
      isERC20: true,
      decimals: 18,
    },
    {
      token: 'UNI',
      networkKey: 'sepolia',
      chainId: 'eip155:11155111',
      network: sepolia,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Sepolia (ERC20)',
      isERC20: true,
      decimals: 18,
    },
  ],
  MATIC: [
    {
      token: 'MATIC',
      networkKey: 'polygon',
      chainId: 'eip155:137',
      network: polygon,
      adapterType: 'wagmi',
      namespace: 'eip155',
      networkName: 'Polygon',
    },
  ],
};

/**
 * 宣告內容用途說明與單號關聯
 * @description 將輸入 networkName 正規化為映射查詢使用的 networkKey。
 * @purpose 統一不同來源的網路名稱輸入，避免同義字造成映射查詢失敗。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export function normalizeNetworkNameForLookup(networkName: string): string {
  const normalized = networkName.trim().toLowerCase();
  if (!normalized) {
    return normalized;
  }
  if (normalized === 'ethereum' || normalized === 'ethereum mainnet') {
    return 'mainnet';
  }
  return normalized;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依據幣種與網路名稱查詢單一映射項目。
 * @purpose 提供支付與簽章流程在執行前取得正確 chain/network/adapter 設定。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export function getMappingByCurrencyAndNetwork(
  currency: string,
  networkName: string,
): TokenNetworkAdapterMapping | null {
  const normalizedNetwork = normalizeNetworkNameForLookup(networkName);
  const normalizedCurrency = currency.trim();
  if (!normalizedCurrency) {
    return null;
  }
  const arr = TOKEN_NETWORK_ADAPTER_MAPPING[normalizedCurrency];
  if (!arr?.length) {
    return null;
  }
  return arr.find((m) => m.networkKey === normalizedNetwork) ?? null;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依 token 取得預設映射（陣列第一筆）以相容既有呼叫端。
 * @purpose 在未指定網路時提供向後相容的查詢入口。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
export function getTokenMapping(token: string): TokenNetworkAdapterMapping | null {
  const arr = TOKEN_NETWORK_ADAPTER_MAPPING[token];
  if (!arr?.length) {
    return null;
  }
  return arr[0] ?? null;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 從多個 token 匯總所需 AppKit networks，並依 chainId 去除重複。
 * @purpose 支援多幣種場景的一次性 network 初始化設定。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
export function getNetworksFromTokens(tokens: string[]): AppKitNetwork[] {
  const networkMap = new Map<string, AppKitNetwork>();
  tokens.forEach((token) => {
    const arr = TOKEN_NETWORK_ADAPTER_MAPPING[token];
    if (!arr?.length) {
      return;
    }
    arr.forEach((m) => {
      if (!networkMap.has(m.chainId)) networkMap.set(m.chainId, m.network);
    });
  });
  return Array.from(networkMap.values());
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 從多個 token 匯總並去重 adapter 類型。
 * @purpose 讓初始化流程僅建立實際需要的 adapter 實例集合。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
export function getAdapterTypesFromTokens(tokens: string[]): AdapterType[] {
  const set = new Set<AdapterType>();
  tokens.forEach((token) => {
    const arr = TOKEN_NETWORK_ADAPTER_MAPPING[token];
    arr?.forEach((m) => set.add(m.adapterType));
  });
  return Array.from(set);
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 從 CAIP 字串（CAIP-2/CAIP-10）解析 namespace。
 * @purpose 統一鏈命名空間辨識，供後續路由與協定分流邏輯使用。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
export function parseNamespace(caipString: string): string | null {
  const parts = caipString.split(':');
  return parts.length > 0 ? parts[0] : null;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 從 CAIP-10 地址解析 namespace，並重用通用解析函式。
 * @purpose 提供語意化入口給以地址為輸入的呼叫端。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
export function parseNamespaceFromCaipAddress(caipAddress: string): string | null {
  return parseNamespace(caipAddress);
}

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:16:58.680Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Refactored comments only into required three-section layout, added standardized JSDoc tags, linked declaration-level externals strictly from provided origin tickets, converted all @external entries to full Jira browse URLs, and preserved runtime code unchanged.
 */
